'use client';

import { FC, useMemo, useState } from 'react';
import { Content, isFilled } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextImage } from '@prismicio/next';

/**
 * Renders `description_highlight` text, replacing `[disable]...[/disable]`
 * markers with spans that opt out of the default purple highlight color
 * (inheriting the neutral body color instead).
 */
const renderHighlightWithDisable = (text: string) => {
  const parts = text.split(/(\[disable\][^]*?\[\/disable\])/g);
  return parts.map((part, i) => {
    const match = part.match(/^\[disable\]([^]*?)\[\/disable\]$/);
    if (match) {
      return (
        <span key={i} className="text-[#041118]">
          {match[1]}
        </span>
      );
    }
    return <span key={i}>{part}</span>;
  });
};

/**
 * Props for `FeatureListAccordion`.
 */
export type FeatureListAccordionProps =
  SliceComponentProps<Content.FeatureListAccordionSlice>;

/**
 * Component for "FeatureListAccordion" Slices.
 */
const FeatureListAccordion: FC<FeatureListAccordionProps> = ({ slice }) => {
  const { tag, heading, subheading, items = [] } = slice.primary;
  const purpleLabel = (slice.primary as unknown as { purple_label?: string })
    .purple_label;
  const [openItems, setOpenItems] = useState<boolean[]>(() =>
    items.map(() => true),
  );

  const hasItems = useMemo(() => items.length > 0, [items.length]);

  const toggleItem = (index: number) => {
    setOpenItems((prev) =>
      prev.map((isOpen, i) => (i === index ? !isOpen : isOpen)),
    );
  };

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-[#FAFAFA] px-4 py-[80px] md:px-8 lg:px-[150px]"
    >
      <div className="mx-auto w-full max-w-[1140px]">
        <div className="flex w-full items-center gap-[60px]">
          {tag && (
            <div className="shrink-0 rounded-full border border-[#4b0fea] px-[16px] py-[8px]">
              <p className="whitespace-nowrap text-center text-[14px] font-normal uppercase leading-[1.2] text-[#041118]">
                {tag}
              </p>
            </div>
          )}
          <div className="h-px flex-1 bg-[#cccccc]" />
        </div>

        <div className="mt-[40px] flex w-full flex-col gap-[80px]">
          <div className="flex w-full flex-col gap-[16px] text-[#041118]">
            {isFilled.richText(heading) && (
              <PrismicRichText
                field={heading}
                components={{
                  heading1: ({ children }) => (
                    <h2 className="w-full text-[32px] font-semibold capitalize leading-[1.15] lg:text-[42px]">
                      {children}
                    </h2>
                  ),
                  heading2: ({ children }) => (
                    <h2 className="w-full text-[32px] font-semibold capitalize leading-[1.15] lg:text-[42px]">
                      {children}
                    </h2>
                  ),
                }}
              />
            )}

            {subheading && (
              <p className="w-full text-[16px] font-light leading-[1.5] text-[#041118]">
                {subheading}
              </p>
            )}

            {purpleLabel && (
              <p className="mt-[24px] w-full text-[18px] font-bold leading-[1.5] text-[#4b0fea] md:text-[20px]">
                {purpleLabel}
              </p>
            )}
          </div>

          {hasItems && (
            <div className="flex w-full flex-col">
              {items.map((item, index) => {
                const isOpen = openItems[index] ?? true;

                return (
                  <button
                    key={index}
                    type="button"
                    onClick={() => toggleItem(index)}
                    className={`w-full border-t border-[#cccccc] py-[24px] text-left ${index === items.length - 1 ? 'border-b' : ''}`}
                  >
                    <div className="flex w-full flex-col gap-[8px]">
                      <div className="flex w-full items-center gap-[24px]">
                        <div className="flex size-[57px] shrink-0 items-center justify-center rounded-[8px] bg-[#d7ede3] p-[8px]">
                          {isFilled.image(item.icon) ? (
                            <PrismicNextImage
                              field={item.icon}
                              className="size-[36px] object-contain"
                              fallbackAlt=""
                            />
                          ) : null}
                        </div>

                        <div className="min-w-0 flex-1">
                          <p className="w-full text-[20px] font-semibold leading-[1.5] text-[#041118] md:text-[22px]">
                            {item.title}
                          </p>
                        </div>

                        <span className="relative block size-[33px] shrink-0 rounded-full border border-[#868686]">
                          <span className="absolute left-1/2 top-1/2 h-[1.5px] w-[14px] -translate-x-1/2 -translate-y-1/2 bg-[#868686]" />
                          {!isOpen && (
                            <span className="absolute left-1/2 top-1/2 h-[14px] w-[1.5px] -translate-x-1/2 -translate-y-1/2 bg-[#868686]" />
                          )}
                        </span>
                      </div>

                      {isOpen && (
                        <div className="w-full pl-0 lg:pl-[469px]">
                          <div className="rounded-[8px] bg-[#f4f4f4] p-[24px]">
                            {isFilled.richText(item.description) && (
                              <PrismicRichText
                                field={item.description}
                                components={{
                                  paragraph: ({ children }) => (
                                    <p className="text-[16px] font-light leading-[1.5] text-[#041118]">
                                      {children}
                                    </p>
                                  ),
                                  list: ({ children }) => (
                                    <ul className="list-disc space-y-1 pl-5 text-[16px] font-light leading-[1.5] text-[#041118]">
                                      {children}
                                    </ul>
                                  ),
                                  oList: ({ children }) => (
                                    <ol className="list-decimal space-y-1 pl-5 text-[16px] font-light leading-[1.5] text-[#041118]">
                                      {children}
                                    </ol>
                                  ),
                                }}
                              />
                            )}

                            {(() => {
                              const labeledLines =
                                (
                                  item as unknown as {
                                    labeled_lines?: {
                                      label?: string;
                                      text?: string;
                                    }[];
                                  }
                                ).labeled_lines ?? [];
                              return labeledLines.length > 0 ? (
                                <div className="mt-3 flex flex-col gap-2">
                                  {labeledLines.map((line, lineIdx) =>
                                    line.label || line.text ? (
                                      <p
                                        key={`labeled-line-${lineIdx}`}
                                        className="text-[16px] font-light leading-[1.5] text-[#041118]"
                                      >
                                        {line.label && (
                                          <span className="font-semibold text-[#4b0fea]">
                                            {line.label}
                                          </span>
                                        )}
                                        {line.label && line.text && ' '}
                                        {line.text && <span>{line.text}</span>}
                                      </p>
                                    ) : null,
                                  )}
                                </div>
                              ) : null;
                            })()}

                            {item.description_highlight && (
                              <p className="mt-2 text-[18px] font-medium leading-[1.5] text-[#4b0fea]">
                                {renderHighlightWithDisable(
                                  item.description_highlight,
                                )}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default FeatureListAccordion;
